import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RealtimeDBService, getAllInstances, getAllCourses as getAllCoursesFS, deleteInstance } from '../../../firebase/unifiedDataService';
import { useUnifiedAuth } from '../../../context/UnifiedAuthContext';
import { USER_ROLES } from '../../../config/userRoles';
import { Card, Button } from '../../../components/ui/BaseComponents';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { AlertBanner } from '../../../components/ui/CommonComponents';
import { Logger } from '../../../utils/logger';

const log = Logger.create('QuizzesTab');

function QuizzesTab() {
  const { userProfile } = useUnifiedAuth();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);

  // Reset answers dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [quizToReset, setQuizToReset] = useState(null);

  // Rename state
  const [renamingQuizId, setRenamingQuizId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Fetch quizzes and courses
  useEffect(() => {
    fetchQuizzesAndCourses();
  }, []);

  const fetchQuizzesAndCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch courses from Firestore
      const coursesArray = await getAllCoursesFS();
      const coursesData = {};
      coursesArray.forEach(course => {
        coursesData[course.id] = course;
      });
      setCourses(coursesData);

      // Fetch quiz instances from Firestore
      const instances = await getAllInstances();
      
      if (!instances || instances.length === 0) {
        setQuizzes([]);
        setLoading(false);
        return;
      }

      // Transform instances data
      const quizzesArray = instances.map(instance => {
        // Get course info
        const course = coursesData[instance.courseId];
        const courseName = course?.name || 'Unbekannt';

        // Count questions
        const questionCount = instance.questions?.length || 0;

        // Count answers
        const answerCount = instance.answers 
          ? Object.values(instance.answers).reduce((sum, userAnswers) => 
              sum + Object.keys(userAnswers || {}).length, 0)
          : 0;

        // First question preview
        const firstQuestion = instance.questions && instance.questions.length > 0
          ? instance.questions[0].text || instance.questions[0].question || ''
          : '';

        return {
          id: instance.id,
          title: instance.title || firstQuestion.substring(0, 50) + '...' || `Quiz ${instance.id}`,
          courseId: instance.courseId,
          courseName,
          questionCount,
          answerCount,
          createdAt: instance.startedAt || Date.now(),
          createdBy: instance.userId,
          questions: instance.questions || [],
          answers: instance.answers || {}
        };
      });

      // Sort by creation date (newest first)
      quizzesArray.sort((a, b) => b.createdAt - a.createdAt);

      setQuizzes(quizzesArray);
    } catch (err) {
      log.error('Error fetching quizzes:', err);
      setError('Fehler beim Laden der Quizzes');
    } finally {
      setLoading(false);
    }
  };

  // Check if user can manage quiz
  const canManageQuiz = (quiz) => {
    if (userProfile?.role === USER_ROLES.ADMIN) return true;
    
    const course = courses[quiz.courseId];
    if (course?.owner === userProfile?.uid) return true;
    
    if (quiz.createdBy === userProfile?.uid) return true;

    return false;
  };

  // Filter quizzes
  const filteredQuizzes = quizzes.filter(quiz => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      quiz.title.toLowerCase().includes(searchLower) ||
      quiz.courseName.toLowerCase().includes(searchLower);
    
    const matchesCourse = !courseFilter || quiz.courseId === courseFilter;
    
    return matchesSearch && matchesCourse;
  });

  // Copy quiz link
  const copyQuizLink = async (quizId) => {
    try {
      const baseUrl = window.location.origin;
      const quizUrl = `${baseUrl}/play/${quizId}`;
      await navigator.clipboard.writeText(quizUrl);
      setCopySuccess(quizId);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      log.error('Error copying link:', err);
      setError('Fehler beim Kopieren des Links');
    }
  };

  // Rename quiz
  const startRename = (quiz) => {
    setRenamingQuizId(quiz.id);
    setRenameValue(quiz.title);
  };

  const cancelRename = () => {
    setRenamingQuizId(null);
    setRenameValue('');
  };

  const saveRename = async (quizId) => {
    if (!renameValue.trim()) {
      setError('Titel darf nicht leer sein');
      return;
    }

    try {
      await RealtimeDBService.update(`sessions/${quizId}`, {
        title: renameValue.trim(),
        updatedAt: Date.now()
      });

      setSuccess('Quiz umbenannt');
      setTimeout(() => setSuccess(null), 2000);
      
      // Update local state
      setQuizzes(prev => prev.map(q => 
        q.id === quizId ? { ...q, title: renameValue.trim() } : q
      ));

      setRenamingQuizId(null);
      setRenameValue('');
    } catch (err) {
      log.error('Error renaming quiz:', err);
      setError('Fehler beim Umbenennen');
    }
  };

  // Reset answers
  const handleResetAnswers = async () => {
    if (!quizToReset) return;

    try {
      await RealtimeDBService.remove(`sessions/${quizToReset.id}/answers`);

      setSuccess('Antworten zurückgesetzt');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh quizzes
      await fetchQuizzesAndCourses();
    } catch (err) {
      log.error('Error resetting answers:', err);
      setError('Fehler beim Zurücksetzen der Antworten');
    } finally {
      setQuizToReset(null);
      setResetDialogOpen(false);
    }
  };

  const openResetDialog = (quiz) => {
    setQuizToReset(quiz);
    setResetDialogOpen(true);
  };

  // Delete quiz
  const handleDeleteQuiz = async () => {
    if (!quizToDelete) return;

    try {
      // Delete from Firestore (primary storage)
      await deleteInstance(quizToDelete.id);

      // Delete from Realtime DB (if exists)
      // The path is: quiz_instances/{ownerId}/{instanceId}
      const ownerId = quizToDelete.owner || quizToDelete.userId;
      if (ownerId) {
        await RealtimeDBService.remove(`quiz_instances/${ownerId}/${quizToDelete.id}`).catch(() => {});
      }

      // Also try legacy path just in case
      await RealtimeDBService.remove(`sessions/${quizToDelete.id}`).catch(() => {});

      // Remove session code mapping if exists
      if (quizToDelete.sessionCode) {
        await RealtimeDBService.remove(`session_codes/${quizToDelete.sessionCode}`).catch(() => {});
      }

      setSuccess('Quiz gelöscht');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh quizzes
      await fetchQuizzesAndCourses();
    } catch (err) {
      log.error('Error deleting quiz:', err);
      setError('Fehler beim Löschen des Quiz: ' + err.message);
    } finally {
      setQuizToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const openDeleteDialog = (quiz) => {
    setQuizToDelete(quiz);
    setDeleteDialogOpen(true);
  };

  // Open views
  const openAdminView = (quizId) => {
    navigate(`/live/${quizId}`);
  };

  const openQuizView = (quizId) => {
    navigate(`/play/${quizId}`);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card>
      {/* Alert Messages */}
      {error && (
        <AlertBanner variant="danger" className="mb-4">
          {error}
        </AlertBanner>
      )}
      {success && (
        <AlertBanner variant="success" className="mb-4">
          {success}
        </AlertBanner>
      )}

      {/* Search and Filter */}
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <input
          type="text"
          placeholder="Quizzes durchsuchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary theme-transition"
        />
        <select
          value={courseFilter}
          onChange={(e) => setCourseFilter(e.target.value)}
          className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary theme-transition"
        >
          <option value="">Alle Kurse</option>
          {Object.entries(courses).map(([id, course]) => (
            <option key={id} value={id}>{course.name}</option>
          ))}
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <p className="text-center py-8 text-muted-foreground">Lade Quizzes...</p>
      )}

      {/* Empty State */}
      {!loading && filteredQuizzes.length === 0 && !searchQuery && (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Noch keine Quizzes vorhanden</p>
          <Button onClick={() => navigate('/llm-generator')}>
            Quiz erstellen
          </Button>
        </div>
      )}

      {/* Search Empty State */}
      {!loading && filteredQuizzes.length === 0 && searchQuery && (
        <p className="text-center py-8 text-muted-foreground">
          Keine Quizzes gefunden für "{searchQuery}"
        </p>
      )}

      {/* Quizzes Table */}
      {!loading && filteredQuizzes.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-4 font-medium text-foreground">Quiz-Titel</th>
                <th className="text-left py-3 px-4 font-medium text-foreground">Kurs</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Fragen</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Antworten</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Erstellt</th>
                <th className="text-center py-3 px-4 font-medium text-foreground">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuizzes.map(quiz => (
                <tr 
                  key={quiz.id}
                  className="border-b border-border hover:bg-muted/50 theme-transition"
                >
                  <td className="py-3 px-4">
                    {renamingQuizId === quiz.id ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(quiz.id);
                            if (e.key === 'Escape') cancelRename();
                          }}
                          className="flex-1 px-2 py-1 border border-border rounded bg-background text-foreground text-sm"
                          autoFocus
                        />
                        <Button size="sm" onClick={() => saveRename(quiz.id)}>✓</Button>
                        <Button size="sm" variant="outline" onClick={cancelRename}>✕</Button>
                      </div>
                    ) : (
                      <p className="font-medium text-foreground">{quiz.title}</p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">
                    {quiz.courseName}
                  </td>
                  <td className="py-3 px-4 text-center text-foreground">
                    {quiz.questionCount}
                  </td>
                  <td className="py-3 px-4 text-center text-foreground">
                    {quiz.answerCount}
                  </td>
                  <td className="py-3 px-4 text-center text-muted-foreground text-xs">
                    {formatDate(quiz.createdAt)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {/* Rename Button */}
                      {canManageQuiz(quiz) && renamingQuizId !== quiz.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startRename(quiz)}
                        >
                          Umbenennen
                        </Button>
                      )}

                      {/* Admin View Button */}
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => openAdminView(quiz.id)}
                      >
                        Admin
                      </Button>

                      {/* Quiz View Button */}
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => openQuizView(quiz.id)}
                      >
                        Quiz
                      </Button>

                      {/* Copy Link Button */}
                      <Button
                        variant={copySuccess === quiz.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => copyQuizLink(quiz.id)}
                      >
                        {copySuccess === quiz.id ? "Kopiert" : "Link kopieren"}
                      </Button>

                      {/* Reset Answers Button */}
                      {canManageQuiz(quiz) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openResetDialog(quiz)}
                        >
                          Reset
                        </Button>
                      )}

                      {/* Delete Button */}
                      {canManageQuiz(quiz) && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => openDeleteDialog(quiz)}
                        >
                          Löschen
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setQuizToDelete(null);
        }}
        onConfirm={handleDeleteQuiz}
        title="Quiz wirklich löschen?"
        message={`"${quizToDelete?.title}" wird mit allen Fragen und Antworten gelöscht.\n\nDiese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        variant="danger"
      />

      {/* Reset Answers Confirmation Dialog */}
      <ConfirmDialog
        isOpen={resetDialogOpen}
        onClose={() => {
          setResetDialogOpen(false);
          setQuizToReset(null);
        }}
        onConfirm={handleResetAnswers}
        title="Alle Antworten zurücksetzen?"
        message={`Alle Antworten für "${quizToReset?.title}" werden gelöscht.\n\nDiese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Zurücksetzen"
        variant="warning"
      />
    </Card>
  );
}

export default QuizzesTab;
